import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import UniversalPricing from "@/components/UniversalPricing";
import { VisionBadge, CtaRow } from "@/components/ContactCtas";
import { Toaster, toast } from "sonner";
import { getPlan } from "@/lib/billing";

export default function Billing() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPlan()
      .then(setPlan)
      .catch(() => toast.error("Could not load plan"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar variant="app" />
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Billing</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-ink-900">Your plan</h1>
        <p className="mt-3 text-ink-700 max-w-2xl">SafeForm is included in the Vision subscription.</p>
        <div className="mt-4"><VisionBadge /></div>
        <CurrentPlan plan={plan} loading={loading} />

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">Pricing</div>
            <h2 className="font-display font-black text-3xl tracking-tight text-ink-900">SafeForm is part of Vision.</h2>
            <p className="mt-3 text-sm text-ink-700">To start or change a subscription, contact us and we will set it up for your site.</p>
          </div>
          <div className="lg:col-span-8"><UniversalPricing /></div>
        </div>

        <div className="mt-10" data-testid="billing-ctas"><CtaRow testPrefix="billing" /></div>
      </div>
    </div>
  );
}

function CurrentPlan({ plan, loading }) {
  const active = plan?.plan === "team";
  return (
    <div className="mt-8 border-2 border-ink-900 p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start" data-testid="current-plan">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">Current plan</div>
        <div className="flex items-baseline gap-3">
          <div className="font-display font-black text-4xl tracking-tighter text-ink-900" data-testid="current-plan-name">
            {loading ? "—" : active ? "Subscription" : "Evaluation"}
          </div>
          {active && <span className="font-mono text-[10px] uppercase tracking-[0.3em] bg-safety text-white px-2 py-0.5">Active</span>}
        </div>
        {active && plan?.seats && (
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500" data-testid="current-seats">
            {plan.seats} seat{plan.seats === 1 ? "" : "s"}
          </div>
        )}
        {active && plan?.current_period_end && (
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">
            Renews / ends · {new Date(plan.current_period_end).toLocaleDateString()}
          </div>
        )}
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-2">This month</div>
        <div className="font-display font-black text-4xl tracking-tighter text-ink-900" data-testid="usage-count">
          {loading ? "—" : plan?.this_month_usage ?? 0}
          {!active && plan?.monthly_limit != null && <span className="text-base font-mono text-ink-500"> / {plan.monthly_limit}</span>}
        </div>
        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500">analyses run</div>
      </div>
    </div>
  );
}
